import { ObjectData, ObjectId } from "@novorender/data-js-api";
import pMap from "p-map";

import { byProperty, type ObjectDataAccess } from "dataAccess";
import { CachedObject, PublicId } from "types/misc";

/**
 * If there are fewer than this number of objects - don't use resolve.
 * Search returns more data and speed on this amount of objects won't differ noticeably.
 */
export const RESOLVE_MIN_SIZE = 10;

/** How many values one property query carries, and how many of those run at once. */
const SEARCH_BATCH_SIZE = 500;
const SEARCH_CONCURRENCY = 5;

const PUBLIC_ID_KEY = "Procore/InstanceId";
const PUBLIC_ID_KEY_OLD = "Procore/Id";

export const PUBLIC_ID_NULL_VALUE = "";

/**
 * The objects for these ids, keyed by id.
 *
 * Two ways to the same answer, and the cheap one is tried first: the backend's bulk id
 * mapping ({@link ObjectDataAccess.resolveObjectIdsToInstanceIds}), and an `id` property
 * query when that is unavailable — which it says with `undefined`, so the query is a
 * fallback rather than a second attempt. Every backend holds the mapping natively, so the
 * fallback is for the scene or index that happens not to carry it.
 *
 * @param params.requireBbox Load each object's bounds, which forces the query path — the
 *   mapping answers instance ids and nothing else. Defaults to `false`.
 */
export async function collectAllObjectsByObjectId({
    objectIds,
    dataAccess,
    abortSignal,
    requireBbox = false,
}: {
    objectIds: ObjectId[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    requireBbox?: boolean;
}): Promise<{ objectId2ObjectMap: Map<ObjectId, CachedObject> }> {
    if (objectIds.length === 0) {
        return { objectId2ObjectMap: new Map() };
    }

    if (!requireBbox && objectIds.length >= RESOLVE_MIN_SIZE) {
        const instanceIds = await dataAccess.resolveObjectIdsToInstanceIds(objectIds, abortSignal);
        if (instanceIds) {
            return { objectId2ObjectMap: mappedObjectsByObjectId(objectIds, instanceIds) };
        }
    }

    const objectId2ObjectMap = new Map<ObjectId, CachedObject>();
    await collectMatches({
        dataAccess,
        property: "id",
        values: objectIds.map(String),
        abortSignal,
        onObjects: (objects) => foundObjectsByObjectId(objects, objectId2ObjectMap),
    });

    return { objectId2ObjectMap };
}

/**
 * The objects for these public ids, keyed by the public id each was asked for.
 *
 * {@link collectAllObjectsByObjectId} inverted, with one asymmetry: the bulk mapping knows
 * only `Procore/InstanceId`, so a caller after the legacy `Procore/Id` goes straight to the
 * property query.
 *
 * @param params.useOldPublicId Key on the legacy `Procore/Id` rather than
 *   `Procore/InstanceId`.
 */
export async function collectAllObjectsByPublicId({
    publicIds,
    dataAccess,
    useOldPublicId,
    abortSignal,
}: {
    publicIds: PublicId[];
    dataAccess: ObjectDataAccess;
    useOldPublicId: boolean;
    abortSignal: AbortSignal;
}): Promise<{ publicId2ObjectMap: Map<PublicId, CachedObject> }> {
    if (publicIds.length === 0) {
        return { publicId2ObjectMap: new Map() };
    }

    if (!useOldPublicId && publicIds.length >= RESOLVE_MIN_SIZE) {
        const objectIds = await dataAccess.resolveInstanceIdsToObjectIds(publicIds, abortSignal);
        if (objectIds) {
            return { publicId2ObjectMap: mappedObjectsByPublicId(publicIds, objectIds) };
        }
    }

    const publicId2ObjectMap = new Map<PublicId, CachedObject>();
    await collectMatches({
        dataAccess,
        property: useOldPublicId ? PUBLIC_ID_KEY_OLD : PUBLIC_ID_KEY,
        values: publicIds,
        abortSignal,
        onObjects: (objects) => foundObjectsByPublicId(objects, publicId2ObjectMap, useOldPublicId),
    });

    return { publicId2ObjectMap };
}

/**
 * Every object whose `property` holds one of `values`, handed to `onObjects` a batch at a
 * time.
 *
 * `full`, because the caller reads a property (and the bounds) off every single match —
 * without it each row would cost its own metadata read. A batch that fails contributes
 * nothing rather than failing the others: the caller's answer is a map, so a missing id
 * reads the same as an id the scene does not have.
 */
async function collectMatches({
    dataAccess,
    property,
    values,
    abortSignal,
    onObjects,
}: {
    dataAccess: ObjectDataAccess;
    property: string;
    values: string[];
    abortSignal: AbortSignal;
    onObjects: (objects: ObjectData[]) => void;
}): Promise<void> {
    const batches: string[][] = [];
    for (let from = 0; from < values.length; from += SEARCH_BATCH_SIZE) {
        batches.push(values.slice(from, from + SEARCH_BATCH_SIZE));
    }

    await pMap(
        batches,
        async (batch) => {
            const objects: ObjectData[] = [];
            try {
                for await (const ref of dataAccess.findByProperties(byProperty(property, batch), {
                    full: true,
                    signal: abortSignal,
                })) {
                    // Free on a `full` row; a row whose metadata is unreadable is dropped
                    // rather than losing the batch it came in.
                    const object = await ref.loadMetaData().catch(() => undefined);
                    if (object) {
                        objects.push(object);
                    }
                }
            } catch {
                // Swallow errors to continue processing other batches
            }
            onObjects(objects);
        },
        { concurrency: SEARCH_CONCURRENCY },
    );
}

/**
 * The bulk mapping as cached objects — positional, so an entry is only written for an id
 * the backend actually mapped.
 *
 * `bbox: undefined` is load-bearing: the mapping carries no geometry, so this says "not
 * loaded" where the query path says `null` for "loaded, and there is none".
 */
function mappedObjectsByObjectId(objectIds: ObjectId[], instanceIds: (string | null)[]): Map<ObjectId, CachedObject> {
    const map = new Map<ObjectId, CachedObject>();
    objectIds.forEach((internalId, at) => {
        const publicId = instanceIds[at];
        if (publicId === null || publicId === undefined || publicId === PUBLIC_ID_NULL_VALUE) {
            return;
        }
        map.set(internalId, { internalId, publicId, oldPublicId: undefined, bbox: undefined });
    });
    return map;
}

/** {@link mappedObjectsByObjectId} the other way round. */
function mappedObjectsByPublicId(publicIds: PublicId[], objectIds: (ObjectId | null)[]): Map<PublicId, CachedObject> {
    const map = new Map<PublicId, CachedObject>();
    publicIds.forEach((publicId, at) => {
        const internalId = objectIds[at];
        if (internalId === null || internalId === undefined || publicId === PUBLIC_ID_NULL_VALUE) {
            return;
        }
        map.set(publicId, { internalId, publicId, oldPublicId: undefined, bbox: undefined });
    });
    return map;
}

function foundObjectsByObjectId(objects: ObjectData[], map: Map<ObjectId, CachedObject>) {
    for (const object of objects) {
        map.set(object.id, {
            internalId: object.id,
            publicId: getPublicId(object),
            oldPublicId: getOldPublicId(object),
            bbox: object.bounds?.box ?? null, // set to null to distinguish from undefined (not loaded)
        });
    }
}

function foundObjectsByPublicId(objects: ObjectData[], map: Map<PublicId, CachedObject>, useOldPublicId: boolean) {
    for (const object of objects) {
        const publicId = getPublicId(object);
        const oldPublicId = getOldPublicId(object);
        const key = useOldPublicId ? oldPublicId : publicId;
        if (key === PUBLIC_ID_NULL_VALUE) {
            continue;
        }
        map.set(key, {
            internalId: object.id,
            publicId,
            oldPublicId,
            bbox: object.bounds?.box ?? null, // set to null to distinguish from undefined (not loaded)
        });
    }
}

export function getPublicId(obj: ObjectData): PublicId {
    const publicId = obj.properties.find((prop) => prop[0] === PUBLIC_ID_KEY);
    return publicId ? publicId[1] : PUBLIC_ID_NULL_VALUE;
}

export function getOldPublicId(obj: ObjectData): PublicId {
    const publicId = obj.properties.find((prop) => prop[0] === PUBLIC_ID_KEY_OLD);
    return publicId ? publicId[1] : PUBLIC_ID_NULL_VALUE;
}
